/**
 * Files/Media Manager API Types
 * Types for the user file management system
 */

// ===================
// ENUMS
// ===================

export enum FileCategory {
  Personal = 0,
  Content = 1,
}

// ===================
// HELPER FUNCTIONS
// ===================

export function getCategoryName(category: FileCategory): string {
  switch (category) {
    case FileCategory.Personal:
      return 'Personal'
    case FileCategory.Content:
      return 'Content'
    default:
      return 'Unknown'
  }
}

export function getCategoryColor(category: FileCategory): 'default' | 'primary' | 'secondary' | 'info' {
  switch (category) {
    case FileCategory.Personal:
      return 'primary'
    case FileCategory.Content:
      return 'secondary'
    default:
      return 'default'
  }
}

export function getCategoryIcon(category: FileCategory): 'person' | 'folder' {
  switch (category) {
    case FileCategory.Personal:
      return 'person'
    case FileCategory.Content:
      return 'folder'
    default:
      return 'folder'
  }
}

// ===================
// FILE DTOs
// ===================

/** Single file metadata */
export interface UserFileDto {
  id: string
  fileName: string
  displayName: string | null
  mimeType: string
  fileSize: number
  width: number | null
  height: number | null
  category: FileCategory
  isFavorite: boolean
  folderId: string | null
  folderName: string | null
  usageCount: number
  createdDate: string
  fileUrl: string
  thumbnailUrl: string | null
}

/** @deprecated Use UserFileDetailDto instead */
export interface UserFileWithUsagesDto extends UserFileDto {
  usageCount: number
  usages: MediaFileUsageDto[]
}

/** Folder metadata */
export interface MediaFolderDto {
  id: string
  name: string | null
  description: string | null
  parentFolderId: string | null
  parentFolderName: string | null
  sortOrder: number
  color: string | null
  iconName: string | null
  fileCount: number
  subfolderCount: number
  createdDate: string
  updatedDate: string
}

/** Folder tree structure */
export interface MediaFolderTreeDto {
  id: string
  name: string | null
  description: string | null
  parentFolderId: string | null
  sortOrder: number
  color: string | null
  iconName: string | null
  fileCount: number
  children: MediaFolderTreeDto[] | null
}

/** File usage information */
export interface MediaFileUsageDto {
  id: string
  entityType: string | null
  entityId: string
  entityName: string | null
  usageContext: string | null
  createdDate: string
}

/** File detail with usages */
export interface UserFileDetailDto extends UserFileDto {
  description: string | null
  tags: string[] | null
  usages: MediaFileUsageDto[] | null
}

/** Upload result */
export interface UploadResultDto {
  id: string
  fileName: string | null
  fileUrl: string | null
  thumbnailUrl: string | null
  fileSize: number
  mimeType: string | null
  category: FileCategory
}

/** Move files request */
export interface MoveFilesRequest {
  fileIds: string[]
  targetFolderId: string | null
}

/** Bulk operation result */
export interface BulkOperationResultDto {
  successCount: number
  failureCount: number
  errors: string[] | null
}

/** Storage quota information */
export interface StorageQuotaDto {
  // Personal storage
  personalBytesUsed: number
  personalBytesLimit: number
  personalFileCount: number
  personalFileLimit: number
  personalUsagePercent: number
  personalBytesRemaining: number
  personalFilesRemaining: number
  isPersonalStorageQuotaExceeded: boolean
  isPersonalFileCountExceeded: boolean
  // Content storage
  contentBytesUsed: number
  contentFileCount: number
  // Metadata
  lastUpdated: string
}

// ===================
// PAGINATED RESPONSES
// ===================

export interface UserFilePagedResult {
  files: UserFileDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}


// ===================
// REQUEST TYPES
// ===================

export interface GetFilesParams {
  folderId?: string
  category?: FileCategory
  favoritesOnly?: boolean
  mimeTypePrefix?: string
  searchTerm?: string
  tag?: string
  sortBy?: 'name' | 'date' | 'size'
  sortDesc?: boolean
  page?: number
  pageSize?: number
}

export interface CreateFolderRequest {
  name: string
  parentFolderId?: string | null
  description?: string | null
  color?: string | null
  iconName?: string | null
}

export interface UpdateFolderRequest {
  name?: string
  parentFolderId?: string | null
  description?: string | null
  sortOrder?: number | null
  color?: string | null
  iconName?: string | null
}

export interface UpdateFileRequest {
  displayName?: string | null
  description?: string | null
  folderId?: string | null
  tags?: string[]
  isFavorite?: boolean
}

export interface SelectFileRequest {
  fileId: string
  entityType: string
  entityId: string
}

export interface SelectFileResponse {
  success: boolean
  fileUrl: string
}

// ===================
// UTILITY FUNCTIONS
// ===================

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export function getFileTypeIcon(mimeType: string): 'image' | 'video' | 'audio' | 'document' | 'file' {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  if (
    mimeType.includes('pdf') ||
    mimeType.includes('document') ||
    mimeType.includes('text/') ||
    mimeType.includes('spreadsheet')
  ) {
    return 'document'
  }
  return 'file'
}

export function getFileTypeLabel(mimeType: string): string {
  const type = getFileTypeIcon(mimeType)
  switch (type) {
    case 'image':
      return 'Image'
    case 'video':
      return 'Video'
    case 'audio':
      return 'Audio'
    case 'document':
      return 'Document'
    default:
      return 'File'
  }
}
